import React, { FunctionComponent, memo } from 'react'
import EditText from 'blocks.simple/editText/editText'
import Button from 'blocks.simple/button/button'
import { OtpCodeInput } from 'molecules/OtpCodeInput'
import { cn } from 'ethcss'
import { grid, text as textStyles } from 'theme'
import translate from 'core/translate'
import { isNotEmptyString, trimSegmentsFromString } from 'core/utils/coreUtil'
import {
    SecondFactorLoginMethod,
    isBackupCodeComplete,
    isOtpComplete,
    normalizeBackupCode,
} from 'core/models/SecondFactor'
import { secondFactorLoginStepStyles as styles } from './SecondFactorLoginStep-styles'

const formatBackupCodeForSubmit = (raw: string) => {
    const normalized = normalizeBackupCode(raw)
    if (normalized.length < 8) {
        return normalized
    }
    return `${normalized.slice(0, 4)}-${normalized.slice(4, 8)}`
}

const METHOD_META: Record<
    SecondFactorLoginMethod,
    { titleKey: string; hintKey: string; placeholderKey: string; kind: 'primary' | 'recovery' }
> = {
    email: {
        titleKey: 'secondFactorMethodEmail',
        hintKey: 'confirmationEmailLoginText',
        placeholderKey: 'confirmEmailLoginPlaceholder',
        kind: 'primary',
    },
    totp: {
        titleKey: 'secondFactorMethodTotp',
        hintKey: 'totpLoginHint',
        placeholderKey: 'totpLoginPlaceholder',
        kind: 'primary',
    },
    backup_code: {
        titleKey: 'secondFactorMethodBackup',
        hintKey: 'backupCodeLoginHint',
        placeholderKey: 'backupCodeLoginPlaceholder',
        kind: 'recovery',
    },
}

export interface SecondFactorLoginStepProps {
    email: string
    code: string
    prefix: string | null
    method: SecondFactorLoginMethod
    availableMethods: SecondFactorLoginMethod[]
    isLoading: boolean
    compact?: boolean
    onChangeCode: (code: string) => void
    onChangeMethod: (method: SecondFactorLoginMethod) => void
    onConfirm: (codeOverride?: string) => void
    onResendEmail?: () => void
}

const SecondFactorLoginStepComponent: FunctionComponent<SecondFactorLoginStepProps> = ({
    email,
    code,
    prefix,
    method,
    availableMethods,
    isLoading,
    compact,
    onChangeCode,
    onChangeMethod,
    onConfirm,
    onResendEmail,
}) => {
    const meta = METHOD_META[method]
    const primaryMethods = availableMethods.filter((item) => METHOD_META[item].kind === 'primary')
    const canUseBackup = availableMethods.includes('backup_code') && method !== 'backup_code'
    const showBackupReturn = method === 'backup_code' && availableMethods.includes('totp')

    const handleChangeCode = (value: string) => {
        if (method === 'email' && prefix) {
            onChangeCode(trimSegmentsFromString(value, [`${prefix}-`, '_']))
            return
        }
        onChangeCode(value.trim())
    }

    const submitCode = (codeOverride?: string) => {
        if (isLoading) {
            return
        }
        if (method === 'backup_code') {
            onConfirm(formatBackupCodeForSubmit(codeOverride ?? code))
            return
        }
        onConfirm(codeOverride)
    }

    const isConfirmEnabled = () => {
        if (isLoading) {
            return false
        }
        switch (method) {
            case 'email':
                return prefix
                    ? isNotEmptyString(trimSegmentsFromString(code, [`${prefix}-`, '_']))
                    : isNotEmptyString(code)
            case 'totp':
                return isOtpComplete(code)
            case 'backup_code':
                return isBackupCodeComplete(code)
            default:
                return isNotEmptyString(code)
        }
    }

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault()
                if (isConfirmEnabled()) {
                    submitCode()
                }
            }}
        >
            <div className={styles.email}>{email}</div>

            {primaryMethods.length > 1 && (
                <div className={styles.methods}>
                    {primaryMethods.map((availableMethod) => (
                        <button
                            key={availableMethod}
                            type="button"
                            className={cn(styles.methodBtn, {
                                [styles.methodBtnActive]: availableMethod === method,
                            })}
                            onClick={() => onChangeMethod(availableMethod)}
                        >
                            {translate(METHOD_META[availableMethod].titleKey)}
                        </button>
                    ))}
                </div>
            )}

            <div>
                {method === 'totp' ? (
                    <OtpCodeInput
                        id="code_login_otp"
                        value={code}
                        onChange={onChangeCode}
                        onComplete={(value) => submitCode(value)}
                        autoFocus
                    />
                ) : method === 'backup_code' ? (
                    <OtpCodeInput
                        id="code_login_backup"
                        value={code}
                        length={8}
                        mode="alphanumeric"
                        onChange={onChangeCode}
                        onComplete={(value) => submitCode(value)}
                        autoComplete="off"
                        autoFocus
                    />
                ) : (
                    <EditText
                        id={'code_login'}
                        className={cn(textStyles.center)}
                        placeholder={translate(meta.placeholderKey)}
                        mod={compact ? undefined : 'withBottomLine'}
                        rounded={compact ? 'full_md' : 'none'}
                        indentSize={compact ? 'tiny' : undefined}
                        value={code}
                        mask={prefix ? `${prefix}-****` : undefined}
                        onChange={handleChangeCode}
                    />
                )}
            </div>

            <div className={styles.hint}>{translate(meta.hintKey)}</div>

            {method === 'email' && onResendEmail && (
                <div className={styles.link} onClick={onResendEmail}>
                    {translate('confirmationEmailLoginLink')}
                </div>
            )}

            {canUseBackup && method === 'totp' && (
                <div className={styles.link} onClick={() => onChangeMethod('backup_code')}>
                    {translate('useBackupCodeInstead')}
                </div>
            )}

            {showBackupReturn && (
                <div className={styles.link} onClick={() => onChangeMethod('totp')}>
                    {translate('useAuthenticatorCodeInstead')}
                </div>
            )}

            {!compact && (
                <div className={cn(grid.row, grid.center, grid.mt_mdPlus)}>
                    <Button
                        disabled={!isConfirmEnabled()}
                        type="submit"
                        mod="secondary"
                        className={cn(grid.row, grid.center, grid.w70)}
                    >
                        {translate('joining')}
                    </Button>
                </div>
            )}
        </form>
    )
}

export const SecondFactorLoginStep = memo(SecondFactorLoginStepComponent)
